/**
 * Relational Profile - Complete Data Model
 * 
 * Comprehensive relationship intelligence across all connection types:
 * - Executive relationships
 * - Internal team members
 * - Partners/Vendors
 * - Clients/Prospects
 * - Mentors/Advisors
 */

export interface RelationalProfile {
  personId: string;
  userId: string;
  
  // ===== IDENTITY =====
  identity: {
    fullName: string;
    preferredName?: string;
    pronunciation?: string;
    pronouns?: string;
    timezone?: string;
    homeBase?: string;
    languages?: string[];
  };
  
  // ===== CONTACT =====
  contact: {
    emails: string[];
    phones?: string[];
    messagingApps?: Array<{
      app: string;
      handle: string;
    }>;
    social?: Array<{
      platform: string;
      handle: string;
    }>;
    channelPreferences?: {
      priorityOrder: string[];
      bestWindows?: string[];
      dnd?: boolean;
    };
  };
  
  // ===== RELATIONSHIP =====
  relationship: {
    types: string[]; // ['client', 'advisor', 'partner', etc.]
    owner?: string;
    strength: number; // 0-10
    origin?: string;
    graph?: {
      introducer?: string;
      influencers?: string[];
    };
  };
  
  // ===== HOUSEHOLD CONTEXT =====
  householdContext?: {
    partnerName?: string;
    children?: Array<{
      name: string;
      age?: number;
    }>;
    pets?: Array<{
      name: string;
      type: string;
    }>;
  };
  
  // ===== BACKGROUND =====
  background?: {
    company?: string;
    title?: string;
    industry?: string;
    education?: string[];
    previousRoles?: Array<{
      company: string;
      title: string;
      years?: string;
    }>;
  };
  
  // ===== PREFERENCES =====
  preferences?: {
    foodDrink?: {
      diet?: string;
      coffeeOrder?: string;
      favoriteSpirit?: string;
    };
    hobbies?: string[];
    travel?: {
      seat?: string;
      hotelChain?: string;
    };
    notes?: string; // Custom notes field
  };
  
  // ===== WORK STYLE =====
  workStyle?: {
    decisionStyle?: string;
    meetingPrefs?: string;
    docFormat?: string;
  };
  
  // ===== VALUES & BOUNDARIES =====
  valuesBoundaries?: {
    coreValues?: string[];
    dealBreakers?: string[];
    ethicalStances?: string[];
  };
  
  // ===== LOGISTICS =====
  logistics?: {
    venues?: string[];
    inPersonOk?: boolean;
  };
  
  // ===== MILESTONES =====
  milestones?: Array<{
    name: string;
    date: string;
  }>;
  
  // ===== COMMITMENTS =====
  commitments?: Array<{
    description: string;
    dueDate?: string;
    status: 'pending' | 'completed' | 'overdue';
  }>;
  
  // ===== CONSENTS =====
  consents?: {
    contact?: boolean;
    photo?: boolean;
    marketing?: string; // 'email-only', 'all', 'none'
  };
  
  // ===== DERIVED SIGNALS =====
  derivedSignals?: {
    lastContact?: string;
    nextPlannedTouch?: string;
    healthScore?: number;
    responsivenessIndex?: number;
    reciprocityBalance?: number;
  };
  
  // ===== AUTOMATION TRIGGERS =====
  automationTriggers?: Array<{
    trigger: string;
    action: string;
  }>;
  
  // ===== DATA QUALITY =====
  dataQuality?: {
    sourceOfTruth?: string;
    confidence?: number;
    lastVerified?: string;
  };
  
  // ===== METADATA =====
  createdAt: string;
  updatedAt: string;
}

/**
 * Context Mode - Adapts UI based on relationship type
 */
export type ContextMode = 'executive' | 'internal' | 'partner' | 'client' | 'mentorship';

export interface ContextModeConfig {
  mode: ContextMode;
  purpose: string;
  metrics: string[];
}

export const CONTEXT_MODES: Record<ContextMode, ContextModeConfig> = {
  executive: {
    mode: 'executive',
    purpose: 'Company/political capital & strategic alignment',
    metrics: ['Influence Map', 'Trust Score', 'Alignment Delta', 'Leadership Tone']
  },
  internal: {
    mode: 'internal',
    purpose: 'Collaboration health & energy',
    metrics: ['Engagement', 'Conflict Index', 'Project Throughput']
  },
  partner: {
    mode: 'partner',
    purpose: 'Operational reliability',
    metrics: ['SLA Adherence', 'Communication Cadence']
  },
  client: {
    mode: 'client',
    purpose: 'Commercial + strategic',
    metrics: ['Fit', 'Timing', 'Opportunity Signals']
  },
  mentorship: {
    mode: 'mentorship',
    purpose: 'Growth + guidance',
    metrics: ['Interaction Cadence', 'Trust', 'Reciprocity']
  }
};

/**
 * Intelligence Layers
 */
export interface IntelligenceLayers {
  narrativeSummary: string;
  suggestedNextStep: {
    action: string;
    reason: string;
    template?: string;
  };
  trustForecast?: {
    trend: 'strengthening' | 'maintaining' | 'drifting';
    confidence: number;
    nextReview: string;
  };
  crossNetworkSignal?: {
    bridgeScore: number;
    connectedGroups: string[];
    lossImpact?: number;
  };
}

/**
 * Interaction with comprehensive metadata
 */
export interface Interaction {
  id: string;
  date: string;
  channel: 'email' | 'call' | 'meeting' | 'message' | 'note';
  topic?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  outcomes?: string[];
  nextStep?: {
    task: string;
    due: string;
  };
  evidenceSnippet?: string;
}

/**
 * Decision Context - for any relationship type
 */
export interface DecisionContext {
  owner?: string;
  relationshipType?: string; // 'deal', 'collaboration', 'hiring', etc.
  objective?: string;
  valueAtStake?: number;
  stage?: string;
  riskLevel?: 'low' | 'medium' | 'high';
  timeline?: string;
  benchmark?: {
    tierMedian: number;
    percentile: number;
  };
}

/**
 * Demo Contact - Fully populated for user preview
 */
export const DEMO_RELATIONAL_PROFILE: RelationalProfile = {
  personId: 'demo_person_001',
  userId: 'demo_user',
  identity: {
    fullName: 'Alexandra Chen',
    preferredName: 'Alex',
    pronunciation: 'al-ex-AN-druh chen',
    pronouns: 'she/her',
    timezone: 'America/Los_Angeles',
    homeBase: 'San Francisco, CA',
    languages: ['English', 'Mandarin']
  },
  contact: {
    emails: ['alex.chen@example.com', 'alex@personal.com'],
    phones: ['+1-415-555-0123'],
    messagingApps: [
      { app: 'Slack', handle: '@alex' },
      { app: 'WhatsApp', handle: '+14155550123' }
    ],
    social: [
      { platform: 'LinkedIn', handle: 'alexandra-chen' },
      { platform: 'Twitter', handle: '@alexchen' }
    ],
    channelPreferences: {
      priorityOrder: ['email', 'slack', 'phone'],
      bestWindows: ['Tue 9-11am', 'Thu 2-4pm'],
      dnd: false
    }
  },
  relationship: {
    types: ['client', 'advisor'],
    owner: 'Sarah Johnson',
    strength: 8,
    origin: 'Introduced by Jane at SaaStr 2025',
    graph: {
      introducer: 'person_jane_001',
      influencers: ['person_cto_001', 'person_vp_001']
    }
  },
  householdContext: {
    partnerName: 'Michael Chen',
    children: [
      { name: 'Emma', age: 7 },
      { name: 'Lucas', age: 4 }
    ],
    pets: [
      { name: 'Luna', type: 'Golden Retriever' }
    ]
  },
  background: {
    company: 'TechCorp Inc.',
    title: 'VP of Engineering',
    industry: 'Enterprise SaaS',
    education: ['MIT - Computer Science', 'Stanford MBA'],
    previousRoles: [
      { company: 'StartupX', title: 'CTO', years: '2018-2022' },
      { company: 'BigTech', title: 'Senior Engineer', years: '2015-2018' }
    ]
  },
  preferences: {
    foodDrink: {
      diet: 'Vegetarian',
      coffeeOrder: 'Oat milk latte, no sugar',
      favoriteSpirit: 'Japanese whisky'
    },
    hobbies: ['Trail running', 'Photography', 'Sourdough baking'],
    travel: {
      seat: 'Aisle',
      hotelChain: 'Marriott Bonvoy'
    }
  },
  workStyle: {
    decisionStyle: 'Data-driven with gut check',
    meetingPrefs: 'Async memo > live meeting when possible',
    docFormat: 'Slides over long docs'
  },
  valuesBoundaries: {
    coreValues: ['Transparency', 'Work-life balance', 'Continuous learning'],
    dealBreakers: ['Dishonesty', 'Micromanagement'],
    ethicalStances: ['Open source advocate', 'Privacy-first']
  },
  logistics: {
    venues: ['Blue Bottle on Market St', 'The Battery (SF)'],
    inPersonOk: true
  },
  milestones: [
    { name: 'Work anniversary', date: '2025-11-15' },
    { name: 'Birthday', date: '1985-06-22' },
    { name: 'Emma\'s graduation', date: '2026-06-10' }
  ],
  commitments: [
    {
      description: 'Intro to Head of Product',
      dueDate: '2025-11-05',
      status: 'pending'
    },
    {
      description: 'Review Q4 strategy deck',
      dueDate: '2025-10-28',
      status: 'completed'
    }
  ],
  consents: {
    contact: true,
    photo: false,
    marketing: 'email-only'
  },
  derivedSignals: {
    lastContact: '2025-10-20',
    nextPlannedTouch: '2025-11-03',
    healthScore: 82,
    responsivenessIndex: 0.8,
    reciprocityBalance: 0.6 // Slightly in your favor
  },
  automationTriggers: [
    { trigger: 'No contact for 14 days', action: 'Send check-in reminder' },
    { trigger: 'Commitment approaching due date', action: 'Nudge notification' }
  ],
  dataQuality: {
    sourceOfTruth: 'self-reported + email extraction',
    confidence: 0.9,
    lastVerified: '2025-10-21'
  },
  createdAt: '2025-01-15T10:00:00Z',
  updatedAt: '2025-10-21T14:30:00Z'
};
